import type { RevenueDefinition, RevenueLedgerVersion } from '@cortex/domain-ac-mens-hub-api';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLedgerDefinitions } from '../../../hooks/useRevenueLedger.js';

/**
 * 版の承認の記録（承認者と決定日）。
 * @graph-connects none
 */
interface ApprovalRecord {
  approver: string;
  decidedOn: string;
}

/**
 * 定義の版の一覧から、版の承認の記録を引く。承認者か決定日が空なら記録なしとする
 * （毎朝の計算は承認の記録が空の版を流さない。要件 VR-9）。
 *
 * @param version 選んでいる版
 * @param definitions 定義の版の一覧
 * @returns 承認の記録（一覧に無い・空欄があれば null）
 * @graph-connects none
 */
function approvalRecordOf(
  version: RevenueLedgerVersion,
  definitions: readonly RevenueDefinition[],
): ApprovalRecord | null {
  const definition = definitions.find((d) => d.version === version);
  if (definition === undefined) return null;
  const { approver, decidedOn } = definition;
  if (approver === null || approver.trim() === '' || decidedOn === null) return null;
  return { approver, decidedOn };
}

/** @graph-connects none */
const TEXT = 'text-xs text-[var(--color-text-secondary)]';

/**
 * 承認の記録が無い・読めないことを、警告のアイコンつきで出す。
 * @graph-connects none
 */
function WarningNote({ id, children }: { id: string; children: string }) {
  return (
    <p id={id} className="flex items-center gap-1 text-xs text-[var(--color-text-primary)]">
      <AlertTriangle
        className="h-3.5 w-3.5 shrink-0 text-[var(--color-warning)]"
        aria-hidden="true"
      />
      {children}
    </p>
  );
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上ページの版の選択欄の横に、選んでいる版の承認者と決定日を出す（要件 R-5）。値は定義の版の一覧の記録をそのまま出し、画面に名前を持たない。v0（旧Hub）は会計の定義の版ではないので承認の記録を探さず「承認の記録なし（旧Hub・会計定義と異なる）」と出す。一覧に無い・承認者か決定日が空の版は「承認の記録がありません」と警告つきで出し、読み込み中・取得の失敗はその状態として出す（記録なしと取り違えないため）。
 * @graph-connects useLedgerDefinitions [calls] 定義の版の一覧（定義タブと同じ取得）
 * @graph-connects approvalRecordOf [calls] 選んでいる版の承認の記録
 * @graph-connects WarningNote [calls] 承認の記録が無い・読めないときの表示
 */
export function RevenueVersionApproval({
  id,
  version,
}: {
  /** 版の選択欄の aria-describedby に渡す id */
  id: string;
  version: RevenueLedgerVersion;
}) {
  const { t } = useTranslation();
  const isLegacy = version === 'v0';
  const query = useLedgerDefinitions(!isLegacy);

  if (isLegacy) {
    return <WarningNote id={id}>{t('revenue.approval.legacy')}</WarningNote>;
  }
  if (query.isLoading) {
    return (
      <p id={id} className={TEXT}>
        {t('revenue.approval.loading')}
      </p>
    );
  }
  if (query.isError || query.data === undefined) {
    return <WarningNote id={id}>{t('revenue.approval.fetchError')}</WarningNote>;
  }
  const record = approvalRecordOf(version, query.data.definitions);
  if (record === null) {
    return <WarningNote id={id}>{t('revenue.approval.unrecorded', { version })}</WarningNote>;
  }
  return (
    <dl id={id} className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${TEXT}`}>
      <div className="flex gap-1">
        <dt>{t('revenue.definitions.approver')}</dt>
        <dd className="text-[var(--color-text-primary)]">{record.approver}</dd>
      </div>
      <div className="flex gap-1">
        <dt>{t('revenue.definitions.decidedOn')}</dt>
        <dd className="text-[var(--color-text-primary)]">{record.decidedOn}</dd>
      </div>
    </dl>
  );
}
